import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/7-lombard";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const sevenLombard: CaseStudy = {
  slug: "7-lombard",
  title: "Семёрочка",
  cardTitle: "Разработка сайта ломбарда «Семёрочка» с онлайн-оценкой",
  subtitle: "Сеть ломбардов",
  industry: { label: "Финансовые услуги", slug: "finance" },
  eyebrow: "Корпоративный сайт · Финансовые услуги",
  h1: "Разработка сайта ломбарда с онлайн-оценкой",
  lead:
    "Разработали сайт сети ломбардов «Семёрочка»: онлайн-оценку и калькулятор стоимости изделия, SEO-структуру услуг по видам залога, карту филиалов, тарифы и программу лояльности.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт финансовой компании" },

  seo: {
    primaryKeyword: "разработка сайта ломбарда с онлайн-оценкой",
    secondaryKeywords: [
      "сайт ломбарда разработка",
      "онлайн калькулятор для ломбарда",
      "сайт ювелирного ломбарда",
      "разработка сайта финансовых услуг",
    ],
    semanticEntities: [
      "сеть ломбардов",
      "онлайн-оценка",
      "калькулятор веса и пробы",
      "филиалы сети",
      "тарифы и акции",
      "программа лояльности",
      "версия для слабовидящих",
      "SEO-структура услуг",
    ],
    excludedKeywords: ["ломбард рядом", "сдать золото", "займ под золото", "цена золота"],
    title: "Сайт ломбарда «Семёрочка» — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт сети «Семёрочка» с онлайн-оценкой, калькулятором стоимости изделия, тарифами, офисами, акциями и программой лояльности.",
    h1: "Разработка сайта ломбарда с онлайн-оценкой",
    slug: "7-lombard",
    canonicalUrl: `${SITE_URL}/cases/7-lombard`,
    ogTitle: "Сайт ломбарда «Семёрочка» с онлайн-оценкой",
    ogDescription:
      "Онлайн-оценка, калькулятор стоимости, филиалы, тарифы, акции и программа лояльности сети «Семёрочка».",
    ogImage: `${SITE_URL}${dir}/7-lombard-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Структура услуг, калькулятор и заявки",
    },
    relatedIndustry: {
      label: "Сайты и маркетинг для ломбардов",
      to: routes.lombard,
      note: "Отраслевые решения для ломбардного бизнеса",
    },
    relatedCases: [
      {
        label: "Сайт сети ломбардов «Ломбард-Л1»",
        to: "/cases/lombard-l1",
        note: "Похожий проект в финансовой отрасли",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Давайте обсудим, как мы можем масштабировать ваш бизнес",
    text: "Оставьте заявку — свяжемся в течение двух часов, чтобы задать вопросы и обсудить задачу.",
    button: "Обсудить сайт финансовой компании",
  },

  client: {
    name: "Семёрочка",
    url: "https://7-lombard.ru/",
    city: "Россия",
    industry: "Финансовые услуги",
  },
  year: "2024",
  services: [
    "Разработка корпоративного сайта",
    "Онлайн-оценка и калькулятор стоимости",
    "SEO-структура услуг",
    "Карта филиалов",
    "Тарифы и программа лояльности",
  ],
  platform: "Собственная разработка",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
    { label: "Поддержка", to: routes.corporate },
  ],
  facts: [
    { label: "Клиент", value: "Семёрочка" },
    { label: "Отрасль", value: "Финансовые услуги" },
    { label: "Тип проекта", value: "Сайт ломбарда с онлайн-оценкой" },
    { label: "Год", value: "2024" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Онлайн-оценка и калькулятор стоимости изделия по весу и пробе",
      "SEO-структура услуг по типам залога, пробам и категориям изделий: золото, бриллианты, монеты, часы",
      "Карта и адреса сети из 53 филиалов по России",
      "Тарифы, акции и программа лояльности",
      "Версия сайта для слабовидящих",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "У сети 53 филиала и больше 10 лет работы, поэтому сайту нужен был рабочий конверсионный механизм — онлайн-оценка изделия ещё до визита в отделение. Второй по важности задачей была SEO-структура: десятки категорий по типу залога, пробе и виду изделия сложены в понятную архитектуру с логичной вложенностью.",
        "Дополнительно нужно было показать масштаб сети через карту филиалов и закрыть базовые требования по тарифам, акциям, программе лояльности и доступности сайта.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: фирменная подложка вместо белого фона",
    paragraphs: [
      "Интерфейс построен на тёмно-графитовой и мягкой фирменной подложке — так калькулятор оценки и карточки категорий выделяются, не теряясь на белом фоне.",
      "Калькулятор стоимости вынесен на первый экран как главный элемент конверсии.",
    ],
    bullets: [
      "Тёмно-графитовая фирменная подложка",
      "Калькулятор оценки на первом экране",
      "Единый шаблон для десятков категорий услуг",
    ],
  },

  identity: {
    fonts: [
      { name: "Intro", role: "Заголовки", sample: "Семёрочка" },
      { name: "Intro", role: "Текст и навигация", sample: "Онлайн-оценка изделия" },
    ],
    colors: [
      { hex: "#1E1F22", name: "Основной фон" },
      { hex: "#F2F2F0", name: "Текст на тёмном" },
      { hex: "#D4A94F", name: "Акцент бренда" },
    ],
  },

  cover: img(
    "7-lombard-case-cover",
    "Главная страница сайта ломбарда «Семёрочка» с калькулятором онлайн-оценки",
    6,
  ),

  gallery: [
    img("7-lombard-calculator", "Онлайн-оценка и калькулятор стоимости изделия на сайте Семёрочка", 3),
    img("7-lombard-seo-structure", "SEO-структура услуг по типам залога, пробам и изделиям", 3),
    img("7-lombard-branches", "Карта и адреса филиалов сети Семёрочка", 6),
  ],

  responsive: {
    text: [
      "Калькулятор оценки и выбор филиала спроектированы для мобильного сценария в первую очередь: минимум полей и переход к результату за один экран.",
      "Смысловые блоки — тарифы, акции, доступность — на мобильном расположены горизонтальным скроллом.",
    ],
    images: [
      mobile("7-lombard-mobile-home", "Мобильная главная сайта Семёрочка с калькулятором"),
      mobile("7-lombard-mobile-tariffs", "Мобильные тарифы и программа лояльности"),
    ],
  },

  columnBlocks: [
    {
      id: "calculator",
      title: "Онлайн-оценка",
      paragraphs: [
        "Калькулятор веса и пробы — главный конверсионный механизм сайта: посетитель получает предварительную оценку изделия до визита в отделение и переходит к обращению в ближайший филиал.",
      ],
      bullets: ["Расчёт по весу и пробе", "Категории изделий: золото, бриллианты, монеты, часы", "Переход к обращению в филиал"],
    },
    {
      id: "seo-tree",
      title: "SEO-дерево услуг и филиалы",
      paragraphs: [
        "Архитектура сайта построена по логике «тип залога → проба → изделие», что позволяет системно закрывать десятки категорий услуг без дублирования контента. Отдельный блок — карта и адреса 53 филиалов сети с локальным сценарием обращения.",
      ],
      columns: [
        { title: "Тип залога", items: ["Ювелирные изделия", "Техника"] },
        { title: "Проба и изделие", items: ["Золото", "Бриллианты", "Монеты", "Часы"] },
        { title: "Филиалы", items: ["53 отделения по России", "Карта и адреса", "Обращение в филиал"] },
      ],
    },
    {
      id: "trust",
      title: "Тарифы и доступность",
      paragraphs: [
        "Тарифы, акции и программа лояльности собраны в компактном блоке рядом с инструментами доступности — версией сайта для слабовидящих.",
      ],
      bullets: ["Тарифы и акции", "Программа лояльности", "Версия для слабовидящих"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка корпоративного сайта", to: routes.corporate },
      { label: "Поддержка и развитие", to: routes.corporate },
    ],
  },
};
